import { useCallback, useEffect, useRef, useState } from 'react';
import API from 'services/API';
import { Endpoints } from 'UI/constants/endpoints';
import { UIStatus } from 'UI/constants/status';
import { getErrorMessage } from 'UI/utils';

const EMPTY_RESULTS = {
  sendout: null,
  candidate: null
};

const fetchSendout = async sendoutId => {
  const response = await API.get(`${Endpoints.Sendouts}/${sendoutId}`, {
    apiVersion: 1
  });

  return response?.data ?? null;
};

const fetchCandidate = async candidateId => {
  const response = await API.get(`${Endpoints.Candidates}/${candidateId}`, {
    apiVersion: 2
  });

  return response?.data ?? null;
};

export const useSendoutDetailsEditData = ({ sendoutId = null }) => {
  const latestRequestIdRef = useRef(0);
  const [state, setState] = useState({
    status: sendoutId ? UIStatus.Loading : UIStatus.Default,
    results: EMPTY_RESULTS,
    error: null,
    responseStatusCode: null
  });

  const refreshData = useCallback(async () => {
    if (!sendoutId) return;

    const requestId = latestRequestIdRef.current + 1;
    latestRequestIdRef.current = requestId;

    setState(prev => ({
      ...prev,
      status: UIStatus.Loading,
      error: null,
      responseStatusCode: null
    }));

    try {
      const sendout = await fetchSendout(sendoutId);

      let candidate = null;

      if (sendout?.candidate_id) {
        candidate = await fetchCandidate(sendout.candidate_id);
      }

      if (requestId !== latestRequestIdRef.current) return;

      setState({
        status: UIStatus.Success,
        results: {
          sendout,
          candidate
        },
        error: null,
        responseStatusCode: null
      });
    } catch (error) {
      if (requestId !== latestRequestIdRef.current) return;

      setState({
        status: UIStatus.Error,
        results: EMPTY_RESULTS,
        error: getErrorMessage(error),
        responseStatusCode: error?.response?.status || null
      });
    }
  }, [sendoutId]);

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  return {
    state,
    refreshData
  };
};
